/**
 * CVC Customer AI Companion Section
 * Displays a card that opens the AI Assistant panel for asking questions about CVC Customer data
 * Uses AI Hub API (InitiateCopilotChatSession / GetChatSessionMessageResponse) for chat functionality
 */

import { DirectionalHint, Icon, IconButton, Panel, PanelType, Spinner, SpinnerSize, TextField, TooltipHost } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import React, { useRef, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useGetCopilotAgentChatScenariosQuery } from "../../slices/AIHub.slice";
import { ENDPOINTS } from "../../Services/ServiceConfig";
import { ICVCCustomerAICompanionSectionProps, ICVCCustomerAIChatMessage } from "./CVCCustomer.interfaces";

// AI Hub API base URL
const AI_HUB_BASE_URL = `${ENDPOINTS.BASE_URL}aiplayground`;

// CVC Customer Engagements scenario configuration in AI Hub
const CVC_CUSTOMER_SCENARIO_ID = 36;
const CVC_CUSTOMER_SCENARIO_GROUPING = "CVC Customer Engagements";
const CVC_CUSTOMER_SCENARIO_NAME = "CVC Customer Engagement Analysis";

// Interface for AI Hub scenario
interface IAIHubScenario {
    scenarioId: number;
    scenarioGrouping: string;
    scenarioName: string;
    description: string;
    suggestedPrompts?: string;
    groundingInstructions?: string;
    state?: string;
}

// Panel styles matching BWC/PurviewCVC
const PANEL_STYLES = {
    container: {
        display: "flex",
        flexDirection: "column" as const,
        height: "100%",
        background: "#fafbfc",
        overflow: "hidden",
        position: "relative" as const
    },
    header: {
        padding: "16px 20px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flexShrink: 0
    },
    headerIcon: {
        width: "40px",
        height: "40px",
        borderRadius: "10px",
        background: "rgba(255,255,255,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px"
    },
    headerTitle: {
        fontSize: "18px",
        fontWeight: 600
    },
    headerSubtitle: {
        fontSize: "12px",
        opacity: 0.9
    },
    dataSummary: {
        padding: "10px 16px",
        background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
        borderBottom: "1px solid #a7f3d0",
        fontSize: "12px",
        color: "#047857",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexShrink: 0
    },
    infoBanner: {
        padding: "10px 16px",
        background: "#fff8e1",
        borderBottom: "1px solid #ffe082",
        fontSize: "12px",
        color: "#8d6e00",
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        lineHeight: 1.45,
        flexShrink: 0
    },
    infoBannerIcon: {
        fontSize: "14px",
        color: "#f5a623",
        marginTop: "1px",
        flexShrink: 0
    },
    chatContainer: {
        flex: 1,
        overflowY: "auto" as const,
        overflowX: "hidden" as const,
        padding: "20px",
        display: "flex",
        flexDirection: "column" as const,
        gap: "16px",
        minHeight: 0
    },
    messageUser: {
        alignSelf: "flex-end" as const,
        maxWidth: "80%",
        padding: "12px 16px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff",
        borderRadius: "18px 18px 4px 18px",
        fontSize: "14px",
        lineHeight: 1.5,
        boxShadow: "0 2px 8px rgba(102, 126, 234, 0.2)"
    },
    messageAssistant: {
        alignSelf: "flex-start" as const,
        maxWidth: "95%",
        padding: "16px 20px",
        background: "#ffffff",
        borderRadius: "18px 18px 18px 4px",
        fontSize: "14px",
        lineHeight: 1.6,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        border: "1px solid #e5e7eb"
    },
    messageLoading: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        color: "#64748b"
    },
    inputContainer: {
        padding: "12px 16px",
        background: "#ffffff",
        borderTop: "1px solid #e5e7eb",
        display: "flex",
        gap: "10px",
        alignItems: "flex-end",
        flexShrink: 0,
        boxShadow: "0 -2px 8px rgba(0,0,0,0.04)"
    },
    emptyState: {
        flex: 1,
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        textAlign: "center" as const,
        color: "#64748b"
    }
};

// Markdown styles (matching AI Hub display for tables, images, charts)
const markdownStyles = `
    .cvc-customer-ai-markdown h1,
    .cvc-customer-ai-markdown h2,
    .cvc-customer-ai-markdown h3,
    .cvc-customer-ai-markdown h4 {
        margin-top: 16px;
        margin-bottom: 8px;
        font-weight: 600;
        color: #1a1a2e;
    }
    .cvc-customer-ai-markdown h3 {
        font-size: 15px;
        color: #4338ca;
        border-bottom: 2px solid #e0e7ff;
        padding-bottom: 6px;
        margin-top: 20px;
    }
    .cvc-customer-ai-markdown ol,
    .cvc-customer-ai-markdown ul {
        padding-left: 20px;
        margin: 8px 0;
    }
    .cvc-customer-ai-markdown li {
        margin-bottom: 10px;
        line-height: 1.6;
        padding-left: 4px;
    }
    .cvc-customer-ai-markdown strong {
        font-weight: 600;
        color: #1e40af;
        background: linear-gradient(120deg, #e0e7ff 0%, #c7d2fe 100%);
        padding: 1px 6px;
        border-radius: 4px;
    }
    .cvc-customer-ai-markdown p {
        margin: 8px 0;
        line-height: 1.6;
    }
    /* Link styling - simple and clean */
    .cvc-customer-ai-markdown a {
        color: #667eea;
        text-decoration: none;
        word-break: break-all;
        font-size: 12px;
    }
    .cvc-customer-ai-markdown a:hover {
        text-decoration: underline;
        color: #4338ca;
    }
    /* Code block styles */
    .cvc-customer-ai-markdown pre {
        background: #f4f4f4;
        padding: 12px;
        border-radius: 8px;
        overflow-x: auto;
        margin: 12px 0;
        font-family: "Segoe UI Mono", "Consolas", monospace;
        font-size: 13px;
    }
    .cvc-customer-ai-markdown code {
        background: #f4f4f4;
        padding: 2px 6px;
        border-radius: 4px;
        font-family: "Segoe UI Mono", "Consolas", monospace;
        font-size: 13px;
    }
    .cvc-customer-ai-markdown pre code {
        background: transparent;
        padding: 0;
    }
    /* Image styles for charts */
    .cvc-customer-ai-markdown img {
        max-width: 100%;
        border-radius: 8px;
        margin: 12px 0;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    /* Blockquote styles */
    .cvc-customer-ai-markdown blockquote {
        border-left: 4px solid #667eea;
        margin: 12px 0;
        padding: 8px 16px;
        background: #f8f9fa;
        border-radius: 0 8px 8px 0;
        color: #64748b;
    }
`;

/**
 * CVC-specific Markdown component with adaptive table rendering
 * Tables with < 4 columns render as normal tables
 * Tables with >= 4 columns render as vertical cards
 */
let cvcTableHeaders: string[] = [];
let cvcColumnCount = 0;

// Normal table styles for simple tables (< 4 columns)
const normalTableStyles = {
    table: {
        width: "100%",
        borderCollapse: "collapse" as const,
        margin: "16px 0",
        fontSize: "13px",
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
    },
    thead: {
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        color: "#fff"
    },
    th: {
        padding: "12px 16px",
        textAlign: "left" as const,
        fontWeight: 600,
        fontSize: "12px",
        textTransform: "uppercase" as const,
        letterSpacing: "0.5px"
    },
    td: {
        padding: "10px 16px",
        borderBottom: "1px solid #e2e8f0",
        background: "#fff"
    },
    trEven: {
        background: "#f8f9fa"
    }
};

// Smart table component that decides rendering based on column count
const SmartTable = ({ children }: any) => {
    // Count columns from children
    let columnCount = 0;
    const headers: string[] = [];

    React.Children.forEach(children, (child: any) => {
        if (child?.type === 'thead' || child?.props?.node?.tagName === 'thead') {
            React.Children.forEach(child.props.children, (row: any) => {
                if (row?.props?.children) {
                    React.Children.forEach(row.props.children, (th: any) => {
                        columnCount++;
                        if (th?.props?.children) {
                            const headerText = typeof th.props.children === "string"
                                ? th.props.children
                                : React.Children.toArray(th.props.children).map((c: any) =>
                                    typeof c === "string" ? c : c?.props?.children || ""
                                ).join("");
                            headers.push(headerText);
                        }
                    });
                }
            });
        }
    });

    cvcColumnCount = columnCount;
    cvcTableHeaders = headers;

    // Use normal table for < 4 columns
    if (columnCount > 0 && columnCount < 4) {
        return (
            <table style={normalTableStyles.table}>
                {children}
            </table>
        );
    }

    // Use card layout for >= 4 columns
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", margin: "16px 0" }}>
            {children}
        </div>
    );
};

const SmartThead = ({ children }: any) => {
    // Show thead only for normal tables (< 4 columns)
    if (cvcColumnCount > 0 && cvcColumnCount < 4) {
        return <thead style={normalTableStyles.thead}>{children}</thead>;
    }
    return null;
};

const SmartTbody = ({ children }: any) => {
    if (cvcColumnCount > 0 && cvcColumnCount < 4) {
        return <tbody>{children}</tbody>;
    }
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {children}
        </div>
    );
};

const SmartTr = ({ children }: any) => {
    const cells = React.Children.toArray(children);

    // Normal table row for < 4 columns
    if (cvcColumnCount > 0 && cvcColumnCount < 4) {
        return (
            <tr>
                {children}
            </tr>
        );
    }

    // Card layout for >= 4 columns
    const rankCell = cells[0] as React.ReactElement;
    const rankValue = rankCell?.props?.children;

    return (
        <div
            style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "20px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
                border: "1px solid #e2e8f0",
                position: "relative",
                paddingTop: "28px"
            }}
        >
            {/* Rank badge */}
            <div style={{
                position: "absolute",
                top: "-10px",
                left: "16px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                padding: "4px 14px",
                borderRadius: "14px",
                fontSize: "12px",
                fontWeight: 600,
                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)"
            }}>
                #{rankValue}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {cells.slice(1).map((cell: any, idx: number) => {
                    const headerLabel = cvcTableHeaders[idx + 1] || `Column ${idx + 2}`;
                    return (
                        <div key={idx} style={{
                            display: "flex",
                            gap: "12px",
                            paddingBottom: idx < cells.length - 2 ? "12px" : "0",
                            borderBottom: idx < cells.length - 2 ? "1px solid #f1f5f9" : "none"
                        }}>
                            <div style={{
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#64748b",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                minWidth: "120px",
                                flexShrink: 0,
                                paddingTop: "2px"
                            }}>
                                {headerLabel}
                            </div>
                            <div style={{
                                fontSize: "13px",
                                color: "#1e293b",
                                lineHeight: 1.6,
                                flex: 1,
                                wordBreak: "break-word"
                            }}>
                                {cell?.props?.children}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const SmartTh = ({ children }: any) => {
    if (cvcColumnCount > 0 && cvcColumnCount < 4) {
        return <th style={normalTableStyles.th}>{children}</th>;
    }
    return null;
};

const SmartTd = ({ children }: any) => {
    if (cvcColumnCount > 0 && cvcColumnCount < 4) {
        return <td style={normalTableStyles.td}>{children}</td>;
    }
    return <>{children}</>;
};

const cvcMarkdownComponents = {
    table: SmartTable,
    thead: SmartThead,
    tbody: SmartTbody,
    tr: SmartTr,
    th: SmartTh,
    td: SmartTd,
    a: ({ href, children, ...props }: any) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                color: "#667eea",
                textDecoration: "none",
                wordBreak: "break-all",
                fontSize: "12px"
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = "none";
            }}
            {...props}
        >
            {children}
        </a>
    ),
};

/**
 * CVC-specific Markdown renderer with card-based tables
 */
const CVCMarkdownWithCards: React.FC<{ text: string; images?: { key: string; value: string }[] }> = ({ text, images }) => {
    // If there are images, handle them with card-based tables
    if (images && images.length > 0) {
        // Replace image keys with actual images first
        let processedText = text;
        const imageElements: { key: string; element: React.ReactNode }[] = [];

        images.forEach((img, idx) => {
            if (processedText.includes(img.key)) {
                const placeholder = `__IMAGE_${idx}__`;
                processedText = processedText.replace(new RegExp(img.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), placeholder);
                imageElements.push({
                    key: placeholder,
                    element: (
                        <div key={idx} style={{ margin: "8px 0" }}>
                            <img
                                src={`data:image/webp;base64,${img.value}`}
                                alt={img.key}
                                style={{ maxWidth: "100%", borderRadius: "8px" }}
                            />
                        </div>
                    )
                });
            }
        });

        // Split by image placeholders and render
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        const placeholderRegex = /__IMAGE_(\d+)__/g;
        let match;

        while ((match = placeholderRegex.exec(processedText)) !== null) {
            if (match.index > lastIndex) {
                parts.push(
                    <ReactMarkdown key={`md-${lastIndex}`} remarkPlugins={[remarkGfm]} components={cvcMarkdownComponents}>
                        {processedText.slice(lastIndex, match.index)}
                    </ReactMarkdown>
                );
            }
            const imgIdx = parseInt(match[1]);
            parts.push(imageElements[imgIdx]?.element);
            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < processedText.length) {
            parts.push(
                <ReactMarkdown key={`md-${lastIndex}`} remarkPlugins={[remarkGfm]} components={cvcMarkdownComponents}>
                    {processedText.slice(lastIndex)}
                </ReactMarkdown>
            );
        }

        return <div style={{ fontSize: "14px" }}>{parts}</div>;
    }

    return (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={cvcMarkdownComponents}>
            {text}
        </ReactMarkdown>
    );
};

/**
 * Parse sample prompts from AI Hub scenario's suggestedPrompts field
 * Format: "Prompt Title: Prompt description|Another Title: Another description"
 */
const parseSuggestedPrompts = (suggestedPrompts: string | undefined): { id: string; title: string; description: string; prompt: string }[] => {
    if (!suggestedPrompts) return [];

    return suggestedPrompts
        .split("|")
        .map((prompt, index) => prompt.trim())
        .filter(prompt => prompt.length > 0)
        .map((prompt, index) => {
            // Try to split by colon if present for title:description format
            const colonIndex = prompt.indexOf(":");
            let title = prompt;
            let description = prompt;

            if (colonIndex > 0 && colonIndex < 60) {
                title = prompt.substring(0, colonIndex).trim();
                description = prompt.substring(colonIndex + 1).trim();
            }

            return {
                id: `prompt-${index}`,
                title: title.length > 50 ? title.substring(0, 50) + "..." : title,
                description: description.length > 100 ? description.substring(0, 100) + "..." : description,
                prompt: prompt
            };
        });
};

/**
 * Rich Prompt Card Component for Panel
 */
const PROMPT_ICONS = ["BulbSolid", "AreaChart", "SearchData", "Trending12", "TaskList", "AnalyticsReport", "ContactCard", "Group"];
const PROMPT_COLORS = [
    "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    "linear-gradient(135deg, #ec4899 0%, #f472b6 100%)",
    "linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)",
    "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
    "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)",
    "linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)",
    "linear-gradient(135deg, #ef4444 0%, #f87171 100%)"
];

const PromptCard: React.FC<{
    scenario: { id: string; title: string; description: string; prompt: string };
    index: number;
    onClick: () => void;
}> = ({ scenario, index, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    const iconName = PROMPT_ICONS[index % PROMPT_ICONS.length];
    const gradientColor = PROMPT_COLORS[index % PROMPT_COLORS.length];

    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                padding: "14px 16px",
                background: isHovered ? "#f8fafc" : "#ffffff",
                borderRadius: "12px",
                border: isHovered ? "1px solid #667eea" : "1px solid #e2e8f0",
                cursor: "pointer",
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: isHovered ? "translateY(-2px)" : "translateY(0)",
                boxShadow: isHovered
                    ? "0 8px 25px rgba(102, 126, 234, 0.25)"
                    : "0 2px 8px rgba(0, 0, 0, 0.04)",
                position: "relative" as const,
                overflow: "hidden"
            }}
        >
            {/* Decorative gradient bar */}
            <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                background: gradientColor,
                opacity: isHovered ? 1 : 0.6,
                transition: "opacity 0.2s ease"
            }} />

            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <div style={{
                    width: "36px",
                    height: "36px",
                    flexShrink: 0,
                    borderRadius: "10px",
                    background: gradientColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "16px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                }}>
                    <Icon iconName={iconName} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1e293b",
                        marginBottom: "4px",
                        lineHeight: 1.3,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical" as const,
                        overflow: "hidden"
                    }}>
                        {scenario.title}
                    </div>
                    <div style={{
                        fontSize: "11px",
                        color: "#64748b",
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical" as const,
                        overflow: "hidden"
                    }}>
                        {scenario.description}
                    </div>
                </div>
            </div>

            {/* Hover arrow indicator */}
            {isHovered && (
                <div style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#667eea",
                    fontSize: "14px",
                    opacity: 0.8
                }}>
                    <Icon iconName="ChevronRight" />
                </div>
            )}
        </div>
    );
};

/**
 * AI Assistant Panel Component
 */
const CVCCustomerAIAssistantPanel: React.FC<{
    isOpen: boolean;
    onDismiss: () => void;
    initialPrompt?: string;
    messages: ICVCCustomerAIChatMessage[];
    setMessages: React.Dispatch<React.SetStateAction<ICVCCustomerAIChatMessage[]>>;
    sessionId: string;
    setSessionId: (id: string) => void;
    totalCustomers: number;
    totalEngagements: number;
    totalSolutions: number;
    samplePrompts: { id: string; title: string; description: string; prompt: string }[];
    scenarioData: IAIHubScenario | undefined;
}> = ({ isOpen, onDismiss, initialPrompt, messages, setMessages, sessionId, setSessionId, totalCustomers, totalEngagements, totalSolutions, samplePrompts, scenarioData }) => {
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showAllPrompts, setShowAllPrompts] = useState(false);
    const [showPromptGallery, setShowPromptGallery] = useState(false);
    const [showShareToast, setShowShareToast] = useState(false);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const hasProcessedInitialPrompt = useRef(false);

    // Show first 6 prompts by default, or all if showAllPrompts is true
    const visiblePrompts = showAllPrompts ? samplePrompts : samplePrompts.slice(0, 6);

    // Handle share - copy session URL to clipboard
    const handleShare = async () => {
        // Only share if we have a valid server session ID (not the client-generated one)
        if (sessionId && !sessionId.startsWith("cvc-customer-ai-")) {
            const shareUrl = `${window.location.origin}/aihub?sessionId=${sessionId}`;
            try {
                await navigator.clipboard.writeText(shareUrl);
                setShowShareToast(true);
                setTimeout(() => setShowShareToast(false), 3000);
            } catch (error) {
                console.error("Failed to copy share link:", error);
            }
        }
    };

    // Process initial prompt
    React.useEffect(() => {
        if (isOpen && initialPrompt && !hasProcessedInitialPrompt.current) {
            hasProcessedInitialPrompt.current = true;
            handleSendMessage(initialPrompt);
        }
    }, [isOpen, initialPrompt]);

    // Reset processed flag when panel closes
    React.useEffect(() => {
        if (!isOpen) {
            hasProcessedInitialPrompt.current = false;
        }
    }, [isOpen]);

    // Scroll to bottom when new messages arrive
    React.useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTo({
                top: chatContainerRef.current.scrollHeight,
                behavior: "smooth"
            });
        }
    }, [messages]);

    const handleSendMessage = async (question: string) => {
        if (!question.trim() || isLoading) return;

        const userMessage: ICVCCustomerAIChatMessage = { role: "user", content: question };
        setMessages(prev => [...prev, userMessage]);
        setInputValue("");
        setIsLoading(true);

        try {
            const token = sessionStorage.getItem("token");
            let response;

            // Check if this is the first message (no existing sessionId or empty sessionId)
            const isFirstMessage = !sessionId || sessionId.startsWith("cvc-customer-ai-");

            if (isFirstMessage) {
                // Use InitiateCopilotChatSession for first message
                const firstMessagePayload = {
                    description: scenarioData?.description || "CVC Customer Engagement Analysis",
                    groundingInstructions: scenarioData?.groundingInstructions || "",
                    userPrompt: question
                };

                response = await fetch(`${AI_HUB_BASE_URL}/InitiateCopilotChatSession`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        SessionId: "",
                        Message: JSON.stringify(firstMessagePayload),
                        ScenarioId: CVC_CUSTOMER_SCENARIO_ID,
                        ScenarioName: CVC_CUSTOMER_SCENARIO_NAME
                    })
                });
            } else {
                // Use GetChatSessionMessageResponse for subsequent messages
                response = await fetch(`${AI_HUB_BASE_URL}/GetChatSessionMessageResponse`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        SessionId: sessionId,
                        Message: question,
                        ScenarioId: CVC_CUSTOMER_SCENARIO_ID,
                        ScenarioName: CVC_CUSTOMER_SCENARIO_NAME
                    })
                });
            }

            if (!response.ok) {
                throw new Error(`API returned status ${response.status}`);
            }

            const data = await response.json();

            // Update session ID if this was the first message
            if (isFirstMessage && data.sessionId) {
                setSessionId(data.sessionId);
            }

            // Extract AI response from messages array
            if (data.messages && data.messages.length > 0) {
                const aiMessage = data.messages.find((m: any) => m.role !== "user");
                if (aiMessage) {
                    const assistantMessage: ICVCCustomerAIChatMessage = {
                        role: "assistant",
                        content: aiMessage.text || aiMessage.content || "No response received.",
                        images: aiMessage.images || [] // Capture images from AI Hub response
                    };
                    setMessages(prev => [...prev, assistantMessage]);
                }
            }
        } catch (error) {
            const errorMessage: ICVCCustomerAIChatMessage = {
                role: "assistant",
                content: "I apologize, but I encountered an error processing your request. Please try again."
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(inputValue);
        }
    };

    const handleClearConversation = () => {
        setMessages([]);
        setSessionId(`cvc-customer-ai-${Date.now()}`);
        hasProcessedInitialPrompt.current = false;
    };

    const handleScenarioClick = (prompt: string) => {
        handleSendMessage(prompt);
    };

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.custom}
            customWidth="720px"
            headerText=""
            isLightDismiss
            styles={{
                main: {
                    marginTop: 0,
                    boxShadow: "-8px 0 32px rgba(0, 0, 0, 0.12)"
                },
                content: { padding: 0, height: "100%" },
                scrollableContent: { height: "100%", overflowY: "auto", overflowX: "hidden" },
                commands: { display: "none" }
            }}
        >
            <div style={PANEL_STYLES.container}>
                {/* Header */}
                <div style={PANEL_STYLES.header}>
                    <div style={PANEL_STYLES.headerIcon}>
                        <Icon iconName="Robot" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={PANEL_STYLES.headerTitle}>CVC Customer AI Companion</div>
                        <div style={PANEL_STYLES.headerSubtitle}>
                            Ask questions about your CVC Customer data
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "4px" }}>
                        {messages.length > 0 && (
                            <>
                                <TooltipHost content={showShareToast ? "Link copied!" : "Share chat"}>
                                    <IconButton
                                        iconProps={{ iconName: showShareToast ? "CheckMark" : "Share" }}
                                        onClick={handleShare}
                                        disabled={isLoading || sessionId.startsWith("cvc-customer-ai-")}
                                        styles={{
                                            root: {
                                                color: showShareToast ? "#4ade80" : "#fff",
                                                transition: "all 0.2s ease"
                                            },
                                            rootHovered: { color: "#fff", background: "rgba(255,255,255,0.1)" },
                                            rootDisabled: { color: "rgba(255,255,255,0.5)" }
                                        }}
                                    />
                                </TooltipHost>
                                <TooltipHost content="Start new chat">
                                    <IconButton
                                        iconProps={{ iconName: "Add" }}
                                        onClick={handleClearConversation}
                                        disabled={isLoading}
                                        styles={{
                                            root: { color: "#fff" },
                                            rootHovered: { color: "#fff", background: "rgba(255,255,255,0.1)" },
                                            rootDisabled: { color: "rgba(255,255,255,0.5)" }
                                        }}
                                    />
                                </TooltipHost>
                            </>
                        )}
                        <IconButton
                            iconProps={{ iconName: "Cancel" }}
                            onClick={onDismiss}
                            styles={{
                                root: { color: "#fff" },
                                rootHovered: { color: "#fff", background: "rgba(255,255,255,0.1)" }
                            }}
                        />
                    </div>
                </div>

                {/* Data Summary Bar */}
                {totalEngagements > 0 && (
                    <div style={PANEL_STYLES.dataSummary}>
                        <Icon iconName="Database" />
                        <span>
                            Loaded {totalCustomers} unique customers, {totalEngagements} distinct engagements across {totalSolutions} solutions
                        </span>
                    </div>
                )}

                {/* Prompt Scenarios (show when no messages) - Premium Design */}
                {messages.length === 0 && samplePrompts.length > 0 && (
                    <div style={{
                        padding: "20px",
                        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                        borderBottom: "1px solid #e5e7eb",
                        flexShrink: 0
                    }}>
                        {/* Section Header */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "16px"
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px"
                            }}>
                                <div style={{
                                    width: "36px",
                                    height: "36px",
                                    borderRadius: "10px",
                                    background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    fontSize: "16px",
                                    boxShadow: "0 3px 10px rgba(251, 191, 36, 0.3)"
                                }}>
                                    <Icon iconName="BulbSolid" />
                                </div>
                                <div>
                                    <div style={{
                                        fontSize: "15px",
                                        fontWeight: 600,
                                        color: "#1e293b",
                                        marginBottom: "1px"
                                    }}>
                                        Quick Insights
                                    </div>
                                    <div style={{
                                        fontSize: "11px",
                                        color: "#64748b"
                                    }}>
                                        Choose a prompt to get started
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{
                                    fontSize: "11px",
                                    padding: "4px 10px",
                                    background: "linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)",
                                    color: "#4338ca",
                                    borderRadius: "12px",
                                    fontWeight: 600
                                }}>
                                    {samplePrompts.length} available
                                </span>
                                {samplePrompts.length > 6 && (
                                    <button
                                        onClick={() => setShowAllPrompts(!showAllPrompts)}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px",
                                            padding: "6px 12px",
                                            background: showAllPrompts
                                                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                                : "#ffffff",
                                            color: showAllPrompts ? "#fff" : "#667eea",
                                            border: showAllPrompts ? "none" : "1.5px solid #667eea",
                                            borderRadius: "8px",
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            cursor: "pointer",
                                            transition: "all 0.2s ease"
                                        }}
                                    >
                                        <Icon iconName={showAllPrompts ? "ChevronUp" : "ChevronDown"} style={{ fontSize: 10 }} />
                                        {showAllPrompts ? "Less" : `All ${samplePrompts.length}`}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Prompts Grid */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: "10px"
                        }}>
                            {visiblePrompts.map((scenario, index) => (
                                <PromptCard
                                    key={scenario.id}
                                    scenario={scenario}
                                    index={index}
                                    onClick={() => handleScenarioClick(scenario.prompt)}
                                />
                            ))}
                        </div>

                        {/* AI Generated disclaimer */}
                        <div style={{
                            marginTop: "16px",
                            padding: "8px 12px",
                            background: "#f8fafc",
                            borderRadius: "8px",
                            fontSize: "11px",
                            color: "#64748b",
                            textAlign: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px"
                        }}>
                            <Icon iconName="Info" style={{ fontSize: 12 }} />
                            <span>Responses are AI-assisted. Verify critical details.</span>
                        </div>
                    </div>
                )}

                {/* Chat Messages - only render when there are messages */}
                {(messages.length > 0 || isLoading) ? (
                    <div ref={chatContainerRef} style={PANEL_STYLES.chatContainer}>
                        <style>{markdownStyles}</style>
                        {messages.map((msg, index) => (
                            <div
                                key={index}
                                style={msg.role === "user" ? PANEL_STYLES.messageUser : PANEL_STYLES.messageAssistant}
                            >
                                {msg.role === "assistant" ? (
                                    <div className="cvc-customer-ai-markdown">
                                        <CVCMarkdownWithCards text={msg.content} images={msg.images || []} />
                                    </div>
                                ) : (
                                    msg.content
                                )}
                            </div>
                        ))}

                        {isLoading && (
                            <div style={{ ...PANEL_STYLES.messageAssistant, width: "fit-content" }}>
                                <div style={PANEL_STYLES.messageLoading}>
                                    <Spinner size={SpinnerSize.small} />
                                    <span>Analyzing data...</span>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Compact "Ready to help!" empty state - fills remaining space */
                    <div style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "24px",
                        textAlign: "center",
                        minHeight: 0
                    }}>
                        <div style={{
                            width: "56px",
                            height: "56px",
                            borderRadius: "16px",
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "12px",
                            boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)"
                        }}>
                            <Icon iconName="ChatBot" style={{ fontSize: 28, color: "#ffffff" }} />
                        </div>
                        <div style={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#1e293b",
                            marginBottom: "4px"
                        }}>
                            Ready to help!
                        </div>
                        <div style={{
                            fontSize: "13px",
                            color: "#64748b",
                            maxWidth: "280px"
                        }}>
                            Select a prompt above or type your question below
                        </div>
                    </div>
                )}

                {/* Browse More Prompts Button - shown when there are messages */}
                {messages.length > 0 && !showPromptGallery && (
                    <div style={{
                        padding: "12px 16px",
                        borderTop: "1px solid #e2e8f0",
                        background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
                        display: "flex",
                        justifyContent: "center"
                    }}>
                        <button
                            onClick={() => setShowPromptGallery(true)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "10px 20px",
                                background: "#ffffff",
                                border: "1px solid #e0e7ff",
                                borderRadius: "24px",
                                fontSize: "13px",
                                fontWeight: 500,
                                color: "#667eea",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                boxShadow: "0 2px 8px rgba(102, 126, 234, 0.1)"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
                                e.currentTarget.style.color = "#ffffff";
                                e.currentTarget.style.borderColor = "transparent";
                                e.currentTarget.style.boxShadow = "0 4px 16px rgba(102, 126, 234, 0.3)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#ffffff";
                                e.currentTarget.style.color = "#667eea";
                                e.currentTarget.style.borderColor = "#e0e7ff";
                                e.currentTarget.style.boxShadow = "0 2px 8px rgba(102, 126, 234, 0.1)";
                            }}
                        >
                            <Icon iconName="Library" style={{ fontSize: 16 }} />
                            <span>Browse more prompts</span>
                        </button>
                    </div>
                )}

                {/* Prompt Gallery - shown when button is clicked */}
                {showPromptGallery && (
                    <div style={{
                        padding: "16px",
                        borderTop: "1px solid #e2e8f0",
                        background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
                        maxHeight: "300px",
                        overflowY: "auto"
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "12px"
                        }}>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#1e293b"
                            }}>
                                <Icon iconName="Library" style={{ color: "#667eea" }} />
                                <span>Prompt Gallery</span>
                                <span style={{
                                    fontSize: "11px",
                                    padding: "2px 8px",
                                    background: "#e0e7ff",
                                    color: "#4338ca",
                                    borderRadius: "10px",
                                    fontWeight: 500
                                }}>
                                    {samplePrompts.length} available
                                </span>
                            </div>
                            <button
                                onClick={() => setShowPromptGallery(false)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "6px 12px",
                                    background: "transparent",
                                    border: "none",
                                    borderRadius: "8px",
                                    fontSize: "12px",
                                    color: "#64748b",
                                    cursor: "pointer"
                                }}
                            >
                                <Icon iconName="ChevronDown" style={{ fontSize: 12 }} />
                                Hide
                            </button>
                        </div>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: "10px"
                        }}>
                            {samplePrompts.map((scenario, index) => (
                                <PromptCard
                                    key={scenario.id}
                                    scenario={scenario}
                                    index={index}
                                    onClick={() => {
                                        handleSendMessage(scenario.prompt);
                                        setShowPromptGallery(false);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Input Area */}
                <div style={{ flexShrink: 0, background: "#ffffff" }}>
                    <div style={PANEL_STYLES.inputContainer}>
                        <TextField
                            placeholder="Type a message..."
                            value={inputValue}
                            onChange={(_, newValue) => setInputValue(newValue || "")}
                            onKeyPress={handleKeyPress}
                            multiline
                            autoAdjustHeight
                            disabled={isLoading}
                            styles={{
                                root: { flex: 1 },
                                fieldGroup: {
                                    borderRadius: "10px",
                                    border: "1px solid #e5e7eb",
                                    background: "#f9fafb"
                                }
                            }}
                        />
                        <TooltipHost content="Send message" directionalHint={DirectionalHint.topCenter}>
                            <IconButton
                                iconProps={{ iconName: "Send" }}
                                onClick={() => handleSendMessage(inputValue)}
                                disabled={!inputValue.trim() || isLoading}
                                styles={{
                                    root: {
                                        width: "38px",
                                        height: "38px",
                                        borderRadius: "10px",
                                        background: inputValue.trim() ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" : "#e5e7eb",
                                        color: inputValue.trim() ? "#fff" : "#94a3b8"
                                    },
                                    rootHovered: {
                                        background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                                        color: "#fff"
                                    },
                                    rootDisabled: {
                                        background: "#e5e7eb",
                                        color: "#94a3b8"
                                    }
                                }}
                            />
                        </TooltipHost>
                    </div>
                    <div style={{
                        padding: "6px 16px 10px",
                        textAlign: "center",
                        fontSize: "11px",
                        color: "#9ca3af"
                    }}>
                        Powered by AI • Verify critical details
                    </div>
                </div>

                {/* Share Toast Notification */}
                {showShareToast && (
                    <div style={{
                        position: "absolute",
                        bottom: "80px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        padding: "10px 20px",
                        background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        color: "#fff",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: 500,
                        boxShadow: "0 4px 16px rgba(16, 185, 129, 0.3)",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        zIndex: 1000,
                        animation: "fadeInUp 0.3s ease-out"
                    }}>
                        <Icon iconName="CheckMark" style={{ fontSize: 14 }} />
                        Share link copied to clipboard!
                    </div>
                )}

                {/* Animation for toast */}
                <style>{`
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateX(-50%) translateY(10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(-50%) translateY(0);
                        }
                    }
                `}</style>
            </div>
        </Panel>
    );
};

/**
 * Main AI Companion Section Component - Premium Rich Look
 */
const CVCCustomerAICompanionSection: React.FC<ICVCCustomerAICompanionSectionProps> = ({
    totalCustomers,
    totalEngagements,
    totalSolutions
}) => {
    const [isPanelOpen, { setTrue: openPanel, setFalse: closePanel }] = useBoolean(false);
    const [initialPrompt, setInitialPrompt] = useState<string | undefined>();
    const [messages, setMessages] = useState<ICVCCustomerAIChatMessage[]>([]);
    const [sessionId, setSessionId] = useState<string>(`cvc-customer-ai-${Date.now()}`);

    // Fetch scenarios from AI Hub
    const { data: scenarios = [] } = useGetCopilotAgentChatScenariosQuery();

    // Find the CVC Customer Engagement Analysis scenario (ID: 36)
    const cvcScenario = useMemo(() => {
        return scenarios.find(
            (s: any) => s.scenarioId === CVC_CUSTOMER_SCENARIO_ID ||
                (s.scenarioGrouping === CVC_CUSTOMER_SCENARIO_GROUPING && s.scenarioName === CVC_CUSTOMER_SCENARIO_NAME)
        ) as IAIHubScenario | undefined;
    }, [scenarios]);

    // Parse sample prompts from the scenario's suggestedPrompts field
    const samplePrompts = useMemo(() => {
        return parseSuggestedPrompts(cvcScenario?.suggestedPrompts);
    }, [cvcScenario]);

    const handleAskAssistant = () => {
        setInitialPrompt(undefined);
        openPanel();
    };

    return (
        <>
            {/* Compact AI Companion Card */}
            <div style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "16px",
                padding: "20px 24px",
                marginBottom: "20px",
                boxShadow: "0 8px 32px rgba(102, 126, 234, 0.35)",
                position: "relative",
                overflow: "hidden",
                color: "#ffffff"
            }}>
                {/* Decorative circles */}
                <div style={{
                    position: "absolute",
                    top: "-30px",
                    right: "-30px",
                    width: "140px",
                    height: "140px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.1)",
                    pointerEvents: "none"
                }} />
                <div style={{
                    position: "absolute",
                    bottom: "-50px",
                    right: "100px",
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.05)",
                    pointerEvents: "none"
                }} />

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px", position: "relative", zIndex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
                        <div style={{
                            width: 56,
                            height: 56,
                            borderRadius: "14px",
                            background: "rgba(255,255,255,0.2)",
                            backdropFilter: "blur(10px)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 26,
                            flexShrink: 0,
                            boxShadow: "0 4px 16px rgba(0,0,0,0.1)"
                        }}>
                            <Icon iconName="Robot" />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 18, display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                AI Companion
                                <span style={{
                                    fontSize: 11,
                                    padding: "3px 10px",
                                    background: "rgba(255,255,255,0.2)",
                                    borderRadius: 12,
                                    fontWeight: 500
                                }}>
                                    ✨ Powered by AI Hub
                                </span>
                            </div>
                            <div style={{ fontSize: 13, opacity: 0.95, lineHeight: 1.4 }}>
                                Get instant insights about CVC Customer engagements, trends, and analytics
                            </div>
                        </div>
                    </div>

                    {/* Stats pills */}
                    {totalEngagements > 0 && (
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            <div style={{
                                padding: "6px 12px",
                                background: "rgba(255,255,255,0.15)",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: 500,
                                backdropFilter: "blur(4px)"
                            }}>
                                {totalCustomers.toLocaleString()} Customers
                            </div>
                            <div style={{
                                padding: "6px 12px",
                                background: "rgba(255,255,255,0.15)",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: 500,
                                backdropFilter: "blur(4px)"
                            }}>
                                {totalEngagements.toLocaleString()} Engagements
                            </div>
                        </div>
                    )}

                    <button
                        onClick={handleAskAssistant}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "12px 24px",
                            background: "#ffffff",
                            color: "#667eea",
                            borderRadius: "12px",
                            fontSize: "14px",
                            fontWeight: 600,
                            cursor: "pointer",
                            border: "none",
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
                            e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0) scale(1)";
                            e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 0, 0, 0.15)";
                        }}
                    >
                        <Icon iconName="ChatBot" style={{ fontSize: 16 }} />
                        <span>Ask AI Companion</span>
                        <Icon iconName="ChevronRight" style={{ fontSize: 12, opacity: 0.7 }} />
                    </button>
                </div>
            </div>

            <CVCCustomerAIAssistantPanel
                isOpen={isPanelOpen}
                onDismiss={closePanel}
                initialPrompt={initialPrompt}
                messages={messages}
                setMessages={setMessages}
                sessionId={sessionId}
                setSessionId={setSessionId}
                totalCustomers={totalCustomers}
                totalEngagements={totalEngagements}
                totalSolutions={totalSolutions}
                samplePrompts={samplePrompts}
                scenarioData={cvcScenario}
            />
        </>
    );
};

export default CVCCustomerAICompanionSection;
